import './style.css';

// Constants
const DEFAULT_ADDRESS = '900 Villa St, Mountain View, CA';
const CURRENT_TIME = new Date('2025-01-16T10:41:55-08:00'); // Updated to match current time
const WALKING_DISTANCE_METERS = 1000; // About a 12-minute walk
const MAP_STYLES = [
  {
    "elementType": "geometry",
    "stylers": [{"color": "#242f3e"}]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#746855"}]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#242f3e"}]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#d59563"}]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [{"color": "#17263c"}]
  }
];

class LunchRoulette {
  constructor() {
    this.map = null;
    this.currentLocation = null;
    this.restaurants = [];
    this.restaurantMarkers = [];
    this.selectedRestaurantId = null;
    this.isSpinning = false;
    this.initialized = false;

    // Store instance in window for callback
    window.app = this;

    // Load the Maps API script with enhanced error handling
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = '/api/maps';
    script.async = true;
    script.defer = true;
    
    script.onerror = async (error) => {
      console.error('Failed to load Maps API:', error);
      try {
        // Try to get more detailed error information
        const response = await fetch('/api/maps');
        const errorText = await response.text();
        console.error('Maps API Error Details:', errorText);
        
        const mapContainer = document.getElementById('map');
        if (mapContainer) {
          mapContainer.innerHTML = `<div class="error-message">
            Failed to load Google Maps: ${errorText}
            <br>
            Please check if the API key is properly configured.
          </div>`;
        }
      } catch (fetchError) {
        console.error('Error fetching detailed error:', fetchError);
        const mapContainer = document.getElementById('map');
        if (mapContainer) {
          mapContainer.innerHTML = '<div class="error-message">Failed to load Google Maps. Please check if the API key is properly configured.</div>';
        }
      }
    };
    
    document.head.appendChild(script);
  }

  initMap() {
    if (this.initialized) return;
    this.initialized = true;

    // Initialize map with default location
    const defaultLocation = { lat: 37.7749, lng: -122.4194 }; // San Francisco
    this.map = new google.maps.Map(document.getElementById('map'), {
      zoom: 15,
      center: defaultLocation,
      styles: MAP_STYLES
    });

    // Initialize UI elements
    this.addressInput = document.getElementById('address-input');
    this.preferencesInput = document.getElementById('preferences-input');
    this.updateAddressButton = document.getElementById('update-address');
    this.spinButton = document.getElementById('spin-button');
    this.reserveButton = document.getElementById('reserve-button');
    this.rouletteWheel = document.querySelector('.roulette-wheel');
    this.selectedRestaurant = document.getElementById('selected-restaurant');
    this.restaurantDetails = document.getElementById('restaurant-details');
    this.restaurantDetailsExtended = document.getElementById('restaurant-details-extended');
    this.currentTimeElement = document.getElementById('current-time');

    // Add event listeners
    this.updateAddressButton.addEventListener('click', () => this.updateLocation());
    this.addressInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.updateLocation();
      }
    });
    this.spinButton.addEventListener('click', () => this.spinWheel());
    this.reserveButton.addEventListener('click', () => this.makeReservation());

    // Set initial address if provided
    if (this.addressInput.value) {
      this.geocodeAddress(this.addressInput.value);
    }
  }

  updateCurrentTime() {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    this.currentTimeElement.textContent = CURRENT_TIME.toLocaleString('en-US', options);
  }

  async geocodeAddress(address) {
    if (!address) return;

    const params = new URLSearchParams({
      endpoint: 'geocode',
      address: address
    });

    try {
      console.log('Fetching from:', `/api/maps?${params.toString()}`);
      const response = await fetch(`/api/maps?${params.toString()}`);
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response:', responseText);
        throw new Error('Invalid response from server');
      }

      if (data.error) {
        console.error('Geocoding error:', data);
        throw new Error(data.error_message || data.error);
      }

      if (data.status !== 'OK') {
        console.error('Geocoding failed:', data);
        throw new Error(data.error_message || 'Failed to geocode address');
      }

      const result = data.results[0];
      const location = {
        lat: () => result.geometry.location.lat,
        lng: () => result.geometry.location.lng
      };

      this.currentLocation = location;
      this.initializeMapWithLocation(location);
      await this.fetchNearbyRestaurants();
    } catch (error) {
      console.error('Error geocoding address:', error);
      alert(`Could not find the specified address. ${error.message}`);
    }
  }

  initializeMapWithLocation(location) {
    if (!this.map) {
      this.map = new google.maps.Map(document.getElementById('map'), {
        zoom: 15,
        styles: MAP_STYLES
      });
    }

    this.map.setCenter(location);

    if (this.marker) {
      this.marker.setMap(null);
    }

    this.marker = new google.maps.Marker({
      position: location,
      map: this.map,
      title: this.addressInput.value,
      icon: {
        url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
      }
    });
  }

  async updateLocation() {
    const newAddress = this.addressInput.value;
    if (newAddress) {
      await this.geocodeAddress(newAddress);
    }
  }

  async fetchNearbyRestaurants() {
    if (!this.currentLocation) return;

    const params = new URLSearchParams({
      endpoint: 'places',
      location: `${this.currentLocation.lat()},${this.currentLocation.lng()}`,
      radius: WALKING_DISTANCE_METERS,
      type: 'restaurant',
      openNow: true
    });

    if (this.preferencesInput.value.trim()) {
      params.append('keyword', this.preferencesInput.value.trim());
    }

    try {
      const response = await fetch(`/api/maps?${params.toString()}`);
      const data = await response.json();

      if (data.status !== 'OK') {
        throw new Error(data.error_message || 'Failed to fetch restaurants');
      }

      // Clear existing markers
      if (this.restaurantMarkers) {
        this.restaurantMarkers.forEach(marker => marker.setMap(null));
      }
      this.restaurantMarkers = [];

      this.restaurants = data.results.map(place => ({
        name: place.name,
        placeId: place.place_id,
        cuisine: place.types.filter(type => type !== 'restaurant' && type !== 'food' && type !== 'point_of_interest' && type !== 'establishment')
          .map(type => type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '))
          .join(', ') || 'Restaurant',
        rating: place.rating,
        address: place.vicinity,
        location: place.geometry.location,
        isOpen: true,
        price: place.price_level ? '$'.repeat(place.price_level) : 'N/A'
      }));

      // Add markers for all restaurants
      this.restaurants.forEach(restaurant => {
        const marker = new google.maps.Marker({
          position: restaurant.location,
          map: this.map,
          title: restaurant.name,
          icon: {
            url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
          }
        });
        this.restaurantMarkers.push(marker);
      });

    } catch (error) {
      console.error('Error fetching nearby restaurants:', error);
      alert('Failed to fetch nearby restaurants. Please try again.');
    }
  }

  calculateDistance(location) {
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      this.currentLocation,
      location
    );
    const walkingMinutes = Math.round((distance / 1000) * 12); // Assuming 12 min per km walking speed
    return `${walkingMinutes} min walk`;
  }

  async spinWheel() {
    if (this.isSpinning) return;
    this.isSpinning = true;
    
    this.spinButton.disabled = true;
    this.rouletteWheel.classList.add('spinning');
    
    setTimeout(() => {
      const restaurant = this.restaurants[Math.floor(Math.random() * this.restaurants.length)];
      
      this.selectedRestaurant.textContent = restaurant.name;
      this.restaurantDetails.textContent = `${restaurant.cuisine} • ${restaurant.rating}★ • ${this.calculateDistance(restaurant.location)}`;
      
      this.restaurantDetailsExtended.innerHTML = `
        <h3>${restaurant.name}</h3>
        <div class="restaurant-info-grid">
          <p>🍽️ ${restaurant.cuisine}</p>
          <p>💰 ${restaurant.price}</p>
          <p>⭐ ${restaurant.rating} stars</p>
          <p>🚶 ${this.calculateDistance(restaurant.location)}</p>
          <p>📍 ${restaurant.address}</p>
          <p>🕒 ${restaurant.isOpen ? 'Open Now' : 'Closed'}</p>
        </div>
      `;
      
      // Show reservation button and store selected restaurant
      this.reserveButton.style.display = 'block';
      this.selectedRestaurantId = restaurant.placeId;
      
      this.rouletteWheel.classList.remove('spinning');
      this.spinButton.disabled = false;
      this.isSpinning = false;
    }, 3000);
  }

  async makeReservation() {
    if (!this.selectedRestaurantId) return;

    const params = new URLSearchParams({
      endpoint: 'details',
      place_id: this.selectedRestaurantId,
      fields: 'url,website,formatted_phone_number'
    });

    try {
      const response = await fetch(`/api/maps?${params.toString()}`);
      const data = await response.json();

      if (data.status !== 'OK') {
        throw new Error(data.error_message || 'Failed to get restaurant details');
      }

      const result = data.result;

      // Try to open reservation URL in the following order: Google Maps URL, Website, or Phone
      if (result.url) {
        window.open(result.url, '_blank');
      } else if (result.website) {
        window.open(result.website, '_blank');
      } else if (result.formatted_phone_number) {
        window.open(`tel:${result.formatted_phone_number.replace(/\D/g,'')}`);
      } else {
        alert('No reservation information available. Please contact the restaurant directly.');
      }
    } catch (error) {
      console.error('Error getting restaurant details:', error);
      alert('Failed to get reservation information. Please try again.');
    }
  }
}

// Initialize the app
document.addEventListener('DOMContentLoaded', () => {
  const app = new LunchRoulette();
  window.addEventListener('load', () => {
    if (window.google && window.google.maps) {
      app.initMap();
    }
  });
});
